// @ts-nocheck
import React, { useEffect, useContext, useState, useCallback } from 'react'
import Grid from '@material-ui/core/Grid'
import { inject, observer } from 'mobx-react'
import TabSection from './TabSection'
import SearchResults from './SearchResults'
import { getLocaleStr, codeToLocale, getTranslatedTextByKey, sendRequest, setRequestInCookie } from 'utils/utils'
import queryString from 'query-string'
import { StoreContext } from 'contexts/StoreContext'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import Typography from '@material-ui/core/Typography'
import styled from 'styled-components'
import { isEmpty } from 'lodash-es'
import { sendCustomEvent } from 'utils/analytics/analytics'
import ImageComponent from 'themes/ImageComponent'
import { itemNotFoundPopUp } from 'utils/menu/menuUtils'
import { upToTablet } from 'styles/mixins'
import MenuGridHeader from './MenuGridHeader'
import { useStores } from 'hooks/useStores'
import { transformMenuItemToCartItem } from 'mobx/Cart/utils'
import { tenantUrls, TenantUrlPickOptions } from 'utils/api/storefrontUrls'

const StyledTabContainer = styled(Grid)`
	background-color: var(--clear);
	@media (min-width: 760px) {
		max-width: ${({ $isStaticMenu }) => ($isStaticMenu ? '100%' : '1550px')};
	}
`

const StyledGridContainer = styled(Grid)`
	padding-bottom: 0;

	@media (${upToTablet}) {
		padding-bottom: 60px;
	}
`

const MenuGrid = inject(
	'User',
	'Infra',
	'Home',
	'ItemAdditions'
)(
	observer((props) => {
		const { store } = useContext(StoreContext)
		const rest = store.data
		const { User, Infra, Home, ItemAdditions, courseList, mode, ais, app, isGeneratedStatically, isStaticMenu } = props
		const { l: lang, c: chainId } = Infra.appParams
		const { searchResults } = store
		const locale = User.preferredLanguage ? codeToLocale[User.preferredLanguage] : rest.locale
		const [showStoresClosedDialog, setShowStoresClosedDialog] = useState(false)
		const { cartStore, addressManagerV2Store } = useStores()

		const storesClosedDialog = useCallback(() => {
			const title = getTranslatedTextByKey('webviewFlow.storesAreClosed', 'sorry all our stores are currently closed')
			const message = getTranslatedTextByKey('webviewFlow.orderMessage', 'Please order during our working hours')
			props.Infra.setNotification({
				open: true,
				title,
				message,
				topImg: <ImageComponent asset="/images/pastOrdersEmpty.png" />,
				customAction: () => {
					setShowStoresClosedDialog(false)
					props.Infra.closeNotification()
				},
				customButtonType: 'secondary',
				cancelText: getTranslatedTextByKey('menu.backToMenu', 'Back to menu'),
				showCancelButton: false,
				closeAction: true,
				cancelAction: () => {
					setShowStoresClosedDialog(false)
					props.Infra.closeNotification()
				},
			})

			sendCustomEvent({
				category: 'error',
				action: 'notification',
				label: title,
				message,
			})
		}, [props.Infra])

		const sendStoreClosedEvent = async () => {
			sendCustomEvent({
				category: 'store',
				action: 'closed',
				label: `${store.metaData.name[store.metaData.locale]} - ${store.metaData.openAtStr}`,
			})
		}

		useEffect(() => {
			if (showStoresClosedDialog) {
				storesClosedDialog()
				setShowStoresClosedDialog(false)
			}
		}, [showStoresClosedDialog, storesClosedDialog])

		useEffect(() => {
			// the inner openMenuItem() calls inInscription which is dependent upon User.userLocaleLanguage
			if (rest.items && User.userLocaleLanguage) {
				if (courseList) {
					// accommodate express checkout from the user clicking on an item in a carousel in Messenger or WA (NOT from the home-page for web)
					if ((mode && mode === 'editSingle') || (ais && ais === 'expressCheckout')) {
						try {
							const [fastCheckoutItemId] = JSON.parse(courseList)
							if (rest.items[fastCheckoutItemId]) {
								const cartItem = transformMenuItemToCartItem(rest.items[fastCheckoutItemId])
								cartStore.openItemModal({
									item: cartItem,
								})
							} else {
								console.info(`fast checkout item id: ${fastCheckoutItemId} does not exist in the menu!`)
							}
						} catch (e) {
							console.error(e)
						}
					}
				}
			}
		}, [rest, User.userLocaleLanguage, User.session.appid, courseList, mode, ais, cartStore, ItemAdditions.postInit])

		useEffect(() => {
			// a menu loaded via a chat app means the store is open so no need for this code
			if (app === '10') {
				if (store.metaData?.closed) {
					sendStoreClosedEvent()
				}

				if (store.metaData?.openAtStr && store.metaData?.openAtStr !== '') {
					Infra.setNotification({
						title: '',
						message: <Typography variant="caption">{store.metaData.openAtStr}</Typography>,
						okAction: () => {
							Infra.closeNotification()
						},
					})
				}
			}

			// once the page loads, ensure it's at the top of the page
			window.scrollTo({ top: 0, behavior: 'smooth' })

			// check if there is gift for the user,
			// if it is then add find the item and add it to the cart

			const giftItemId = localStorage.getItem('giftId')
			if (giftItemId) {
				const item = rest.items[giftItemId]
				const cartItem = transformMenuItemToCartItem(item)
				if (!item) {
					itemNotFoundPopUp(Infra)
				} else if (isEmpty(item?.variations)) {
					cartStore.addItem(cartItem)
				} else {
					// when the menu loads and the item pop loads
					// it then disappearing sometimes, after adding this timeout it works
					setTimeout(() => {
						cartStore.openItemModal({
							item: cartItem,
						})
					}, 500)
				}
				// removing the item from the LS that it won't be added again after refresh
				localStorage.removeItem('giftId')
			}

			if (Infra.appParams.eCommerce) {
				try {
					const getBranchesList = async () => {
						const urls = await tenantUrls([TenantUrlPickOptions.WebFlowAddressURL])
						const queryParams = `&lang=${lang}&type=getBranchesList&cust=openRest`
						const branchesListResponse = await sendRequest(
							false,
							`${urls.webFlowAddressURL}${queryParams}`,
							'get',
							null,
							null,
							true,
							90000,
							null,
							false
						)
						Home.setBranchesList(branchesListResponse)
					}

					if(!addressManagerV2Store.newLocalizationFlowEnabled) {
						getBranchesList()
					}
				} catch (e) {
					console.error(`MenuGrid.getBranchesList(): ${e}`)
				}
			}
		}, [])

		useEffect(() => {
			// we need to set query params in the cookies because in some flows its not set, and if not set on refresh the session expired.
			const parsed = queryString.parse(window.location.search)
			if (parsed) {
				setRequestInCookie(parsed.request)
			}
		}, [])

		if (rest && rest.name) {
			return (
				<StyledGridContainer container direction="column" data-testid="menu-sections-container">
					{searchResults && <SearchResults results={searchResults} />}

					<MenuGridHeader />

					{/* only show if no search results */}
					{!searchResults &&
						rest.sections.map((_tabSection, _idx) => {
							const sectionItems = _tabSection.itemIds.map((_itemId) => rest.items[_itemId]).filter((id) => id !== undefined)

							return sectionItems?.length ? (
								<StyledTabContainer
									item
									key={_tabSection.id}
									md={isStaticMenu ? 12 : 9 /* static menu page does not have order summary section */}
									style={{ ...(_idx === rest.sections.length - 1 && { paddingBottom: '33px' }) }}
									$isStaticMenu={isStaticMenu}
									data-section-id={`section-${_tabSection.id}`}
									data-testid="menu-section-container"
								>
									<TabSection
										title={getLocaleStr(_tabSection.title, locale)}
										items={sectionItems}
										isGeneratedStatically={isGeneratedStatically}
										isStaticMenu={isStaticMenu}
										currency={rest.currency}
										countryCode={rest.countryCode}
										User={User}
										Infra={Infra}
										locale={locale}
										media={_tabSection.media}
										sectionId={_tabSection.id}
										openAllStoresAreClosedDialog={() => setShowStoresClosedDialog(true)}
									/>
								</StyledTabContainer>
							) : null
						})}
					{/* WORKAROUND (suggested by creator of Material) these components are added and hidden to load the
					related MUI styling in the <head> so they are already present for when they are used in the MenuItemOverlay */}
					<Button style={{ display: 'none' }} />
					<Chip style={{ display: 'none' }} />
				</StyledGridContainer>
			)
		}
		return null
	})
)

export default MenuGrid
